import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";

import {
  FaRegHeart,
  FaHeart,
  FaArrowRight,
  FaSearch,
  FaSlidersH,
  FaSortAmountDown,
  FaTimes,
  FaRedo,
  FaTag,
  FaChevronDown,
  FaMapMarkerAlt,
  FaTruck,
  FaShieldAlt,
  FaBoxOpen,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  useAddsTodoMutation,
  useGetsTodosQuery,
  useDeleteeTodoMutation,
} from "../../redux/slices/todoApiSlice";

const Cart = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // =========================================================
  // FAVORITE MUTATIONS
  // =========================================================

  const [addsTodo] = useAddsTodoMutation();
  const [deleteeTodo] = useDeleteeTodoMutation();

  // =========================================================
  // FAVORITE QUERY
  // =========================================================

  const { data: favoriteData } = useGetsTodosQuery();

  // =========================================================
  // DATA
  // =========================================================

  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // FAVORITE
  // =========================================================

  const [favoriteLoading, setFavoriteLoading] = useState(null);

  /*
    Məhsul ID-lərini saxlayırıq.

    Məhsul əlavə edilən və ya silinən kimi
    ürəyin görünüşü dərhal dəyişir.
  */
  const [favoriteIds, setFavoriteIds] = useState([]);

  // =========================================================
  // FILTERS
  // =========================================================

  const [search, setSearch] = useState("");

  const [sort, setSort] = useState("default");

  const [minPrice, setMinPrice] = useState("");

  const [maxPrice, setMaxPrice] = useState("");

  const [category, setCategory] = useState("");

  const [condition, setCondition] = useState("");

  const [location, setLocation] = useState("");

  const [showFilters, setShowFilters] = useState(false);

  // =========================================================
  // CATEGORY OPTIONS
  // =========================================================

  const categories = [
    "Elektronika",
    "Telefon",
    "Kompüter",
    "Mebel",
    "Avtomobil",
    "Geyim",
    "Ayaqqabı",
    "Ev və bağ",
    "Uşaq məhsulları",
    "İdman",
    "Kitab",
    "Digər",
  ];

  // =========================================================
  // CONDITION OPTIONS
  // =========================================================

  const conditions = [
    "Yeni",
    "Əla vəziyyətdə",
    "Yaxşı vəziyyətdə",
    "İşlənmiş",
    "Təmirə ehtiyacı var",
  ];

  // =========================================================
  // ERROR MESSAGE
  // =========================================================

  const getErrorMessage = (err) => {
    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.data?.message ||
      err?.data?.message ||
      err?.data?.error;

    if (serverMessage) {
      return String(serverMessage);
    }

    if (err?.response?.status === 400) {
      return "Göndərilən məlumatlar düzgün deyil. Filtrləri yoxlayın və yenidən cəhd edin.";
    }

    if (err?.response?.status === 401) {
      return "Sessiyanız başa çatıb. Zəhmət olmasa, hesabınıza yenidən daxil olun.";
    }

    if (err?.response?.status === 403) {
      return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
    }

    if (err?.response?.status === 404) {
      return "Məhsullar üçün tələb olunan məlumat tapılmadı.";
    }

    if (err?.response?.status === 429) {
      return "Çox sayda sorğu göndərildi. Bir neçə saniyə gözləyib yenidən cəhd edin.";
    }

    if (err?.response?.status >= 500) {
      return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
    }

    if (
      err?.code === "ERR_NETWORK" ||
      err?.message?.toLowerCase()?.includes("network")
    ) {
      return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
    }

    if (err?.message) {
      return String(err.message);
    }

    return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
  };

  // =========================================================
  // FAVORITES -> RTK QUERY DATA
  // =========================================================

  useEffect(() => {
    if (!Array.isArray(favoriteData)) {
      setFavoriteIds([]);
      return;
    }

    /*
      VACİB:
      item._id burada favorite qeydinin öz ID-si ola bilər.

      Ona görə məhsul ID-si üçün əvvəl:
      item.productId
      sonra:
      item.product._id

      götürürük.
    */
    const ids = favoriteData
      .map((item) => {
        return (
          item?.productId ||
          item?.product?._id
        );
      })
      .filter(Boolean);

    setFavoriteIds(ids);
  }, [favoriteData]);

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================

  const fetchData = useCallback(
    async (requestedPage = 1) => {
      if (loading) return;

      try {
        setLoading(true);
        setError("");

        const params = new URLSearchParams();

        params.set("page", requestedPage);
        params.set("limit", "24");

        // SEARCH
        if (search.trim()) {
          params.set("search", search.trim());
        }

        // CATEGORY
        if (category) {
          params.set("category", category);
        }

        // CONDITION
        if (condition) {
          params.set("condition", condition);
        }

        // LOCATION
        if (location.trim()) {
          params.set("location", location.trim());
        }

        // MIN PRICE
        if (minPrice !== "") {
          params.set("minPrice", minPrice);
        }

        // MAX PRICE
        if (maxPrice !== "") {
          params.set("maxPrice", maxPrice);
        }

        // SORT
        if (sort !== "default") {
          params.set("sort", sort);
        }

        const url = `${
          process.env.REACT_APP_API_BASE_URL
        }/api/pagination/getpagenations?${params.toString()}`;

        const response = await axios.get(url);

        const products =
          response.data?.allPagination || [];

        const pages = Number(
          response.data?.totalPages || 1
        );

        const count = Number(
          response.data?.totalCount || 0
        );

        setTotalPages(pages);

        setTotalCount(count);

        setData(products);
      } catch (err) {
        console.error(
          "Məhsullar yüklənərkən xəta:",
          err
        );

        const message = getErrorMessage(err);

        setError(message);

        setData([]);

        setTotalPages(1);

        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      search,
      category,
      condition,
      location,
      minPrice,
      maxPrice,
      sort,
    ]
  );

  // =========================================================
  // LOAD PRODUCTS
  // =========================================================

  useEffect(() => {
    fetchData(page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // =========================================================
  // FILTER CHANGE
  // =========================================================

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    fetchData(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    category,
    condition,
    location,
    minPrice,
    maxPrice,
    sort,
  ]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const nextPage = () => {
    if (loading) return;

    if (page >= totalPages) return;

    setPage((prev) => prev + 1);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const previousPage = () => {
    if (loading) return;

    if (page <= 1) return;

    setPage((prev) => prev - 1);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // FAVORITE TOGGLE
  // =========================================================

  const handleAddToFavorie = async (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı. Əməliyyat yerinə yetirilə bilmədi."
      );

      return;
    }

    // Eyni məhsul üzərində əməliyyat gedirsə,
    // ikinci dəfə klikə icazə vermirik.
    if (favoriteLoading === product._id) {
      return;
    }

    /*
      Məhsul hazırda sevimlidədirsə:
      TRUE  -> DELETE
      FALSE -> POST
    */
    const isFavorite = favoriteIds.includes(
      product._id
    );

    try {
      setFavoriteLoading(product._id);

      // =====================================================
      // ALREADY FAVORITE -> DELETE
      // =====================================================

      if (isFavorite) {
        /*
          favoriteData içindən həmin məhsulun
          favorite qeydini tapırıq.

          Çünki DELETE üçün məhsulun _id-si yox,
          favorite qeydinin _id-si lazımdır.
        */
        const favoriteItem = Array.isArray(
          favoriteData
        )
          ? favoriteData.find((item) => {
              const favoriteProductId =
                item?.productId ||
                item?.product?._id;

              return (
                favoriteProductId ===
                product._id
              );
            })
          : null;

        if (!favoriteItem?._id) {
          toast.error(
            "Sevimli məhsulun məlumatı tapılmadı."
          );

          return;
        }

        // ===================================================
        // BACKEND DELETE
        // ===================================================

        await deleteeTodo(
          favoriteItem._id
        ).unwrap();

        // ===================================================
        // REDUX DELETE
        // ===================================================

        dispatch({
          type: "favorie/delete",
          payload: favoriteItem._id,
        });

        // ===================================================
        // IMMEDIATE UI
        // ===================================================

        setFavoriteIds((prev) =>
          prev.filter(
            (id) => id !== product._id
          )
        );

        // ===================================================
        // DELETE TOAST
        // ===================================================

        toast.success(
          `"${product?.title || "Məhsul"}" sevimlilərdən silindi.`
        );

        return;
      }

      // =====================================================
      // NOT FAVORITE -> ADD
      // =====================================================

      const newFavorie = await addsTodo({
        productId: product._id,
      }).unwrap();

      // =====================================================
      // REDUX ADD
      // =====================================================

      dispatch({
        type: "favorie/add",
        payload: newFavorie,
      });

      // =====================================================
      // IMMEDIATE UI
      // =====================================================

      setFavoriteIds((prev) => {
        if (prev.includes(product._id)) {
          return prev;
        }

        return [
          ...prev,
          product._id,
        ];
      });

      // =====================================================
      // ADD TOAST
      // =====================================================

      toast.success(
        `"${product?.title || "Məhsul"}" sevimlilər siyahısına əlavə edildi.`
      );
    } catch (err) {
      console.error(
        "Sevimli məhsul əməliyyatında xəta:",
        err
      );

      const message =
        getErrorMessage(err);

      /*
        Backend əlavə etməyə çalışanda
        "artıq mövcuddur" qaytararsa,
        ürəyi qırmızı saxlayırıq.

        Amma artıq "artıq sevimlilərinizdədir"
        toast göstərmirik.
      */
      const lowerMessage =
        String(message).toLowerCase();

      if (
        !isFavorite &&
        (
          lowerMessage.includes("artıq") ||
          lowerMessage.includes("artiq") ||
          lowerMessage.includes("already") ||
          lowerMessage.includes("exist") ||
          lowerMessage.includes("mövcuddur") ||
          lowerMessage.includes("movcuddur")
        )
      ) {
        setFavoriteIds((prev) => {
          if (
            prev.includes(product._id)
          ) {
            return prev;
          }

          return [
            ...prev,
            product._id,
          ];
        });

        return;
      }

      toast.error(
        `Sevimli məhsul əməliyyatı mümkün olmadı. Səbəb: ${message}`
      );
    } finally {
      setFavoriteLoading(null);
    }
  };

  // =========================================================
  // GO PRODUCT
  // =========================================================

  const goToProduct = (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı. Məhsul səhifəsi açıla bilmədi."
      );

      return;
    }

    navigate(
      `/pagination/${product._id}`
    );
  };

  // =========================================================
  // IMAGE
  // =========================================================

  const getImage = (product) => {
    if (!product) return "";

    if (Array.isArray(product.photo)) {
      return product.photo[0] || "";
    }

    return product.photo || "";
  };

  // =========================================================
  // PRICE
  // =========================================================

  const getPrice = (product) => {
    const price = Number(
      product?.price
    );

    if (Number.isNaN(price)) {
      return 0;
    }

    return price;
  };

  // =========================================================
  // DISCOUNT
  // =========================================================

  const getDiscountPercent = (
    product
  ) => {
    if (
      !product?.discountApplied ||
      product?.originalPrice == null ||
      product?.price == null
    ) {
      return 0;
    }

    const originalPrice = Number(
      product.originalPrice
    );

    const price = Number(
      product.price
    );

    if (
      Number.isNaN(originalPrice) ||
      Number.isNaN(price) ||
      originalPrice <= 0 ||
      price >= originalPrice
    ) {
      return 0;
    }

    return Math.round(
      ((originalPrice - price) /
        originalPrice) *
        100
    );
  };

  // =========================================================
  // ACTIVE FILTER COUNT
  // =========================================================

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (search.trim()) count++;

    if (minPrice !== "") count++;

    if (maxPrice !== "") count++;

    if (category) count++;

    if (condition) count++;

    if (location.trim()) count++;

    if (sort !== "default") count++;

    return count;
  }, [
    search,
    minPrice,
    maxPrice,
    category,
    condition,
    location,
    sort,
  ]);

  // =========================================================
  // RESET
  // =========================================================

  const resetFilters = () => {
    setSearch("");
    setMinPrice("");
    setMaxPrice("");
    setCategory("");
    setCondition("");
    setLocation("");
    setSort("default");

    setPage(1);

    setError("");
  };

  // =========================================================
  // SKELETON
  // =========================================================

  const SkeletonCard = () => {
    return (
      <div className="overflow-hidden rounded-[20px] border border-gray-200 bg-white dark:border-white/[0.07] dark:bg-[#111214]">
        <div className="aspect-[0.88] animate-pulse bg-gray-200 dark:bg-[#1b1d21]" />

        <div className="space-y-3 p-3.5 sm:p-4">
          <div className="h-3.5 w-4/5 animate-pulse rounded bg-gray-200 dark:bg-[#202329]" />

          <div className="h-3.5 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-[#202329]" />

          <div className="h-6 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-[#202329]" />

          <div className="h-9 animate-pulse rounded-xl bg-gray-200 dark:bg-[#202329]" />
        </div>
      </div>
    );
  };

  // =========================================================
  // PRODUCT CARD
  // =========================================================

  const ProductCard = ({
    product,
  }) => {
    const image = getImage(
      product
    );

    const discount =
      getDiscountPercent(product);

    const isFavorite =
      favoriteIds.includes(
        product?._id
      );

    const isFavoriteLoading =
      favoriteLoading ===
      product?._id;

    return (
      <article
        className="
          group relative overflow-hidden rounded-[20px]
          border border-gray-200 bg-white
          transition-all duration-300
          hover:-translate-y-1
          hover:border-gray-300
          hover:shadow-[0_15px_40px_rgba(0,0,0,0.10)]
          dark:border-white/[0.07]
          dark:bg-[#111214]
          dark:hover:border-white/[0.14]
          dark:hover:shadow-[0_20px_50px_rgba(0,0,0,0.35)]
        "
      >
        {/* IMAGE */}

        <div
          className="relative aspect-[0.88] cursor-pointer overflow-hidden bg-gray-100 dark:bg-[#191b1f]"
          onClick={() =>
            goToProduct(product)
          }
        >
          {image ? (
            <img
              src={image}
              alt={
                product?.title ||
                "Məhsul"
              }
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FaTag className="text-3xl text-gray-300 dark:text-gray-700" />

              <span className="sr-only">
                Məhsul şəkli mövcud deyil
              </span>
            </div>
          )}

          {/* GRADIENT */}

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          {/* DISCOUNT */}

          {discount > 0 && (
            <div className="absolute left-3 top-3 rounded-lg bg-red-500 px-2.5 py-1.5 text-[10px] font-black text-white shadow-lg">
              -{discount}%
            </div>
          )}

          {/* FAVORITE */}

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();

              handleAddToFavorie(
                product
              );
            }}
            disabled={
              isFavoriteLoading
            }
            aria-label={
              isFavorite
                ? "Məhsulu sevimlilərdən sil"
                : "Məhsulu sevimlilərə əlavə et"
            }
            className="
              absolute right-3 top-3
              flex h-9 w-9 items-center justify-center
              rounded-full bg-black/45 text-white
              shadow-lg backdrop-blur-md
              transition-all duration-300
              hover:scale-110 hover:bg-white hover:text-black
              disabled:cursor-wait disabled:opacity-50
              sm:h-10 sm:w-10
            "
          >
            {isFavoriteLoading ? (
              <FaSpinner className="animate-spin text-sm" />
            ) : isFavorite ? (
              <FaHeart className="text-sm text-red-500" />
            ) : (
              <FaRegHeart className="text-sm" />
            )}
          </button>

          {/* CONDITION */}

          {product?.condition && (
            <div className="absolute bottom-3 left-3 max-w-[75%] rounded-lg bg-black/55 px-2.5 py-1.5 text-[9px] font-bold text-white backdrop-blur-md">
              {product.condition}
            </div>
          )}
        </div>

        {/* CONTENT */}

        <div className="p-3.5 sm:p-4">
          {/* CATEGORY */}

          {product?.category && (
            <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-blue-500">
              <FaTag className="text-[8px]" />

              <span className="truncate">
                {product.category}
              </span>
            </div>
          )}

          {/* TITLE */}

          <button
            type="button"
            onClick={() =>
              goToProduct(product)
            }
            className="block w-full text-left"
          >
            <h2 className="line-clamp-2 min-h-[40px] text-sm font-bold leading-5 text-gray-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
              {product?.title ||
                "Adsız məhsul"}
            </h2>
          </button>

          {/* BRAND */}

          {(product?.brand ||
            product?.model) && (
            <p className="mt-1 line-clamp-1 text-[10px] font-medium text-gray-400">
              {[
                product.brand,
                product.model,
              ]
                .filter(Boolean)
                .join(" • ")}
            </p>
          )}

          {/* PRICE */}

          <div className="mt-3 flex min-h-[31px] items-center gap-2">
            <span className="text-lg font-black tracking-tight text-gray-950 dark:text-white">
              {product?.price != null
                ? `${Number(
                    product.price
                  ).toLocaleString(
                    "az-AZ"
                  )} ₼`
                : "Qiymət göstərilməyib"}
            </span>

            {discount > 0 &&
              product?.originalPrice !=
                null && (
                <span className="text-xs text-gray-400 line-through">
                  {Number(
                    product.originalPrice
                  ).toLocaleString(
                    "az-AZ"
                  )}{" "}
                  ₼
                </span>
              )}
          </div>

          {/* LOCATION */}

          {product?.location && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
              <FaMapMarkerAlt className="text-[9px]" />

              <span className="line-clamp-1">
                {product.location}
              </span>
            </div>
          )}

          {/* DELIVERY */}

          {product?.delivery && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-gray-400">
              <FaTruck className="text-[9px]" />

              <span className="line-clamp-1">
                {product.delivery}
              </span>
            </div>
          )}

          {/* WARRANTY */}

          {product?.warranty && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-emerald-500">
              <FaShieldAlt className="text-[9px]" />

              <span className="line-clamp-1">
                {product.warranty}
              </span>
            </div>
          )}

          {/* STOCK */}

          {product?.stock != null && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-gray-400">
              <FaBoxOpen className="text-[9px]" />

              <span>
                {product.stock} ədəd
              </span>
            </div>
          )}

          {/* DETAIL */}

          <button
            type="button"
            onClick={() =>
              goToProduct(product)
            }
            className="
              mt-3 flex h-10 w-full
              items-center justify-between
              rounded-xl bg-gray-100 px-3.5
              text-xs font-bold text-gray-700
              transition-all hover:bg-gray-200
              dark:bg-[#1c2533]
              dark:text-gray-200
              dark:hover:bg-[#263246]
            "
          >
            <span>
              Ətraflı bax
            </span>

            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-700 shadow-sm dark:bg-white/10 dark:text-white">
              <FaArrowRight className="text-[9px]" />
            </span>
          </button>
        </div>
      </article>
    );
  };

  // =========================================================
  // EMPTY
  // =========================================================

  // =========================================================
  // EMPTY
  // =========================================================

  const EmptyData = () => {
    const hasFilters = activeFilterCount > 0;

    return (
      <div
        className="
          col-span-full
          relative
          flex min-h-[430px]
          items-center justify-center
          overflow-hidden
          rounded-[28px]
          border border-gray-200
          bg-white
          px-5 py-12
          shadow-sm
          dark:border-white/[0.07]
          dark:bg-[#111214]
        "
      >
        {/* SOFT BACKGROUND DECORATION */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[260px]
            w-[260px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-blue-500/[0.05]
            blur-3xl
            dark:bg-blue-500/[0.07]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -right-16
            -top-16
            h-40
            w-40
            rounded-full
            bg-purple-500/[0.04]
            blur-3xl
            dark:bg-purple-500/[0.05]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -bottom-20
            -left-16
            h-44
            w-44
            rounded-full
            bg-blue-400/[0.04]
            blur-3xl
            dark:bg-blue-400/[0.04]
          "
        />

        {/* CONTENT */}

        <div className="relative z-10 w-full max-w-md text-center">

          {/* ICON */}

          <div className="relative mx-auto mb-6 h-[100px] w-[100px]">

            {/* OUTER GLOW */}

            <div
              className="
                absolute
                inset-0
                rounded-[30px]
                bg-blue-500/10
                blur-xl
                dark:bg-blue-500/10
              "
            />

            {/* ICON BOX */}

            <div
              className="
                relative
                flex
                h-full
                w-full
                items-center
                justify-center
                rounded-[30px]
                border
                border-gray-200
                bg-gradient-to-br
                from-gray-50
                via-white
                to-blue-50
                shadow-[0_12px_35px_rgba(0,0,0,0.06)]
                dark:border-white/[0.07]
                dark:from-[#191b1f]
                dark:via-[#15171b]
                dark:to-blue-500/[0.08]
                dark:shadow-[0_15px_40px_rgba(0,0,0,0.25)]
              "
            >
              <div
                className="
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-2xl
                  bg-blue-500/10
                  text-blue-500
                  dark:bg-blue-500/10
                  dark:text-blue-400
                "
              >
                <FaBoxOpen className="text-[27px]" />
              </div>
            </div>

            {/* SMALL DECORATION */}

            <div
              className="
                absolute
                -right-1
                -top-1
                flex
                h-7
                w-7
                items-center
                justify-center
                rounded-full
                border-4
                border-white
                bg-blue-500
                text-white
                shadow-lg
                dark:border-[#111214]
              "
            >
              <FaSearch className="text-[9px]" />
            </div>

          </div>

          {/* SMALL LABEL */}

          <div
            className="
              mb-2
              text-[10px]
              font-black
              uppercase
              tracking-[0.22em]
              text-blue-500
            "
          >
            Elanlar
          </div>

          {/* TITLE */}

          <h3
            className="
              text-xl
              font-black
              tracking-tight
              text-gray-950
              sm:text-2xl
              dark:text-white
            "
          >
            {hasFilters
              ? "Axtarışınıza uyğun elan yoxdur"
              : "Hələlik burada elan yoxdur"}
          </h3>

          {/* DESCRIPTION */}

          <p
            className="
              mx-auto
              mt-3
              max-w-[390px]
              text-sm
              leading-6
              text-gray-500
              dark:text-gray-400
            "
          >
            {hasFilters
              ? "Seçdiyiniz filtr və axtarış seçimlərinə uyğun heç bir elan tapılmadı. Filtrləri dəyişərək yenidən cəhd edə bilərsiniz."
              : "Bu bölmədə hələ heç bir elan paylaşılmayıb. Yeni elanlar əlavə olunduqca onları burada görə biləcəksiniz."}
          </p>

          {/* ACTION */}

          {hasFilters ? (
            <button
              type="button"
              onClick={resetFilters}
              className="
                group
                mt-6
                inline-flex
                items-center
                gap-2.5
                rounded-2xl
                bg-gray-900
                px-5
                py-3
                text-xs
                font-black
                text-white
                shadow-lg
                shadow-gray-900/10
                transition-all
                duration-300
                hover:-translate-y-0.5
                hover:bg-gray-700
                hover:shadow-xl
                dark:bg-white
                dark:text-black
                dark:shadow-white/5
                dark:hover:bg-gray-200
              "
            >
              <FaRedo
                className="
                  text-[10px]
                  transition-transform
                  duration-300
                  group-hover:rotate-180
                "
              />

              Filtrləri təmizlə
            </button>
          ) : (
            <div
              className="
                mx-auto
                mt-6
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-gray-200
                bg-gray-50
                px-4
                py-2.5
                dark:border-white/[0.07]
                dark:bg-white/[0.03]
              "
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10">
                <FaCheckCircle className="text-[9px] text-emerald-500" />
              </span>

              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">
                Yeni elanlar üçün bizi izləyin
              </span>
            </div>
          )}

          {/* BOTTOM DECORATIVE DOTS */}

          <div className="mt-8 flex items-center justify-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500/30" />
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500/50" />
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500/30" />
          </div>

        </div>
      </div>
    );
  };

  // =========================================================
  // ERROR
  // =========================================================

  const ErrorBox = () => {
    return (
      <div className="col-span-full flex min-h-[350px] items-center justify-center rounded-[24px] border border-red-200 bg-white dark:border-red-500/20 dark:bg-[#111214]">
        <div className="w-full max-w-lg px-5 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-500/10">
            <FaExclamationTriangle className="text-xl text-red-500" />
          </div>

          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Məhsullar yüklənmədi
          </h3>

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Məhsulların yüklənməsi zamanı problem
            yarandı.
          </p>

          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-left dark:border-red-500/20 dark:bg-red-500/5">
            <div className="flex items-start gap-3">
              <FaTimes className="mt-0.5 shrink-0 text-red-500" />

              <div>
                <p className="text-xs font-bold text-red-700 dark:text-red-400">
                  Xətanın səbəbi:
                </p>

                <p className="mt-1 break-words text-xs leading-5 text-red-600 dark:text-red-300">
                  {error ||
                    "Naməlum xəta baş verdi."}
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              fetchData(page)
            }
            disabled={loading}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-xs font-bold text-white transition hover:bg-gray-700 disabled:cursor-wait disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-gray-200"
          >
            {loading ? (
              <FaSpinner className="animate-spin text-[10px]" />
            ) : (
              <FaRedo className="text-[10px]" />
            )}

            {loading
              ? "Yenidən yoxlanılır..."
              : "Yenidən yoxla"}
          </button>
        </div>
      </div>
    );
  };

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <main
      id="all-productsssssssssssss"
      className="min-h-screen bg-[#f6f7f9] px-3 py-5 transition-colors sm:px-5 sm:py-7 lg:px-8 dark:bg-[#08090b]"
    >
      <div className="mx-auto w-full max-w-[1800px]">
        {/* HEADER */}

        <div className="mb-6 flex flex-col gap-4 md:mb-7 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-500">
              Artım-Elan
            </div>

            <h1 className="text-2xl font-black tracking-tight text-gray-950 sm:text-3xl dark:text-white">
              Məhsullar
            </h1>

            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              İstədiyiniz məhsulu tapın və elanlara baxın.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 dark:border-white/[0.07] dark:bg-[#111214]">
              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                {totalCount}
              </span>

              <span className="ml-1.5 text-xs text-gray-400">
                məhsul
              </span>
            </div>

            {activeFilterCount >
              0 && (
              <button
                type="button"
                onClick={
                  resetFilters
                }
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs font-bold text-red-600 transition hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/5 dark:text-red-400"
              >
                <FaTimes className="text-[9px]" />

                {activeFilterCount} aktiv filtr
              </button>
            )}
          </div>
        </div>

        {/* SEARCH */}

        <section className="mb-6 rounded-[22px] border border-gray-200 bg-white p-3 shadow-sm dark:border-white/[0.07] dark:bg-[#111214]">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row">
              {/* SEARCH */}

              <div className="relative flex-1">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xs text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder="Məhsul, marka və ya model axtarın..."
                  aria-label="Məhsul axtarışı"
                  className="
                    h-12 w-full rounded-xl
                    border border-gray-200
                    bg-gray-50 pl-11 pr-10
                    text-sm font-medium
                    text-gray-900 outline-none
                    transition
                    focus:border-blue-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-blue-500/10
                    dark:border-white/[0.07]
                    dark:bg-[#0b0c0e]
                    dark:text-white
                    dark:placeholder:text-gray-600
                  "
                />

                {search && (
                  <button
                    type="button"
                    onClick={() =>
                      setSearch("")
                    }
                    aria-label="Axtarışı təmizlə"
                    className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 dark:bg-white/10 dark:text-gray-400"
                  >
                    <FaTimes className="text-[9px]" />
                  </button>
                )}
              </div>

              {/* SORT */}

              <div className="relative lg:w-[250px]">
                <FaSortAmountDown className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xs text-gray-400" />

                <select
                  value={sort}
                  onChange={(event) =>
                    setSort(
                      event.target.value
                    )
                  }
                  aria-label="Məhsulları sırala"
                  className="
                    h-12 w-full cursor-pointer
                    appearance-none rounded-xl
                    border border-gray-200
                    bg-gray-50 pl-10 pr-10
                    text-sm font-semibold
                    text-gray-800 outline-none
                    transition
                    focus:border-blue-500
                    focus:ring-4
                    focus:ring-blue-500/10
                    dark:border-white/[0.07]
                    dark:bg-[#0b0c0e]
                    dark:text-gray-200
                  "
                >
                  <option value="default">
                    Sıralama
                  </option>

                  <option value="price-low">
                    Qiymət: ucuzdan bahaya
                  </option>

                  <option value="price-high">
                    Qiymət: bahadan ucuza
                  </option>

                  <option value="newest">
                    Ən yeni elanlar
                  </option>
                </select>

                <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[9px] text-gray-400" />
              </div>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    (prev) => !prev
                  )
                }
                aria-expanded={
                  showFilters
                }
                className={`
                  flex h-12 items-center
                  justify-center gap-2
                  rounded-xl border px-5
                  text-sm font-bold
                  transition-all
                  ${
                    showFilters ||
                    activeFilterCount >
                      0
                      ? "border-blue-500 bg-blue-500 text-white"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-white/[0.07] dark:bg-[#0b0c0e] dark:text-gray-300 dark:hover:bg-white/[0.04]"
                  }
                `}
              >
                <FaSlidersH className="text-xs" />

                Filtrlər

                {activeFilterCount >
                  0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[9px] font-black text-blue-600">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* FILTER PANEL */}

            {showFilters && (
              <div className="border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  {/* CATEGORY */}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Kateqoriya
                    </label>

                    <div className="relative">
                      <select
                        value={category}
                        onChange={(event) =>
                          setCategory(
                            event.target.value
                          )
                        }
                        className="
                          h-11 w-full appearance-none
                          rounded-xl
                          border border-gray-200
                          bg-gray-50 px-4 pr-9
                          text-sm font-semibold
                          text-gray-800 outline-none
                          focus:border-blue-500
                          dark:border-white/[0.07]
                          dark:bg-[#0b0c0e]
                          dark:text-white
                        "
                      >
                        <option value="">
                          Bütün kateqoriyalar
                        </option>

                        {categories.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          )
                        )}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-400" />
                    </div>
                  </div>

                  {/* CONDITION */}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Vəziyyət
                    </label>

                    <div className="relative">
                      <select
                        value={condition}
                        onChange={(event) =>
                          setCondition(
                            event.target.value
                          )
                        }
                        className="
                          h-11 w-full appearance-none
                          rounded-xl
                          border border-gray-200
                          bg-gray-50 px-4 pr-9
                          text-sm font-semibold
                          text-gray-800 outline-none
                          focus:border-blue-500
                          dark:border-white/[0.07]
                          dark:bg-[#0b0c0e]
                          dark:text-white
                        "
                      >
                        <option value="">
                          Bütün vəziyyətlər
                        </option>

                        {conditions.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          )
                        )}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-400" />
                    </div>
                  </div>

                  {/* LOCATION */}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Yer
                    </label>

                    <div className="relative">
                      <FaMapMarkerAlt className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400" />

                      <input
                        type="text"
                        value={location}
                        onChange={(event) =>
                          setLocation(
                            event.target.value
                          )
                        }
                        placeholder="Bakı, Gəncə..."
                        aria-label="Yer üzrə filtr"
                        className="
                          h-11 w-full rounded-xl
                          border border-gray-200
                          bg-gray-50 pl-9 pr-3
                          text-sm font-semibold
                          text-gray-900 outline-none
                          focus:border-blue-500
                          dark:border-white/[0.07]
                          dark:bg-[#0b0c0e]
                          dark:text-white
                        "
                      />
                    </div>
                  </div>

                  {/* MIN */}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Minimum qiymət
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={minPrice}
                        onChange={(event) =>
                          setMinPrice(
                            event.target.value
                          )
                        }
                        placeholder="0"
                        aria-label="Minimum qiymət"
                        className="
                          h-11 w-full rounded-xl
                          border border-gray-200
                          bg-gray-50 px-4 pr-10
                          text-sm font-semibold
                          text-gray-900 outline-none
                          focus:border-blue-500
                          dark:border-white/[0.07]
                          dark:bg-[#0b0c0e]
                          dark:text-white
                        "
                      />

                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                        ₼
                      </span>
                    </div>
                  </div>

                  {/* MAX */}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Maksimum qiymət
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={maxPrice}
                        onChange={(event) =>
                          setMaxPrice(
                            event.target.value
                          )
                        }
                        placeholder="5000"
                        aria-label="Maksimum qiymət"
                        className="
                          h-11 w-full rounded-xl
                          border border-gray-200
                          bg-gray-50 px-4 pr-10
                          text-sm font-semibold
                          text-gray-900 outline-none
                          focus:border-blue-500
                          dark:border-white/[0.07]
                          dark:bg-[#0b0c0e]
                          dark:text-white
                        "
                      />

                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                        ₼
                      </span>
                    </div>
                  </div>

                  {/* RESET */}

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={
                        resetFilters
                      }
                      className="
                        flex h-11 w-full
                        items-center justify-center
                        gap-2 rounded-xl
                        border border-gray-200
                        bg-gray-50 px-5
                        text-xs font-bold
                        text-gray-600
                        transition hover:bg-gray-100
                        dark:border-white/[0.07]
                        dark:bg-[#0b0c0e]
                        dark:text-gray-400
                        dark:hover:bg-white/[0.05]
                      "
                    >
                      <FaRedo className="text-[9px]" />

                      Filtrləri sıfırla
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ACTIVE FILTERS */}

        {activeFilterCount >
          0 && (
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Aktiv filtrlər:
            </span>

            {search.trim() && (
              <button
                type="button"
                onClick={() =>
                  setSearch("")
                }
                className="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-[10px] font-bold text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"
              >
                Axtarış: "{search}"

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {category && (
              <button
                type="button"
                onClick={() =>
                  setCategory("")
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Kateqoriya: {category}

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {condition && (
              <button
                type="button"
                onClick={() =>
                  setCondition("")
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Vəziyyət: {condition}

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {location.trim() && (
              <button
                type="button"
                onClick={() =>
                  setLocation("")
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Yer: {location}

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {minPrice !==
              "" && (
              <button
                type="button"
                onClick={() =>
                  setMinPrice("")
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Minimum: {minPrice} ₼

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {maxPrice !==
              "" && (
              <button
                type="button"
                onClick={() =>
                  setMaxPrice("")
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Maksimum: {maxPrice} ₼

                <FaTimes className="text-[8px]" />
              </button>
            )}

            {sort !==
              "default" && (
              <button
                type="button"
                onClick={() =>
                  setSort(
                    "default"
                  )
                }
                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:bg-white/[0.06] dark:text-gray-300"
              >
                Sıralama seçilib

                <FaTimes className="text-[8px]" />
              </button>
            )}
          </div>
        )}

        {/* RESULT INFO */}

        {!loading &&
          !error &&
          data.length > 0 && (
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 dark:text-gray-500">
                {data.length} məhsul göstərilir
              </span>

              <span className="text-[10px] font-semibold text-gray-400">
                Səhifə {page} /{" "}
                {totalPages}
              </span>
            </div>
          )}

        {/* PRODUCTS */}

        {loading &&
        data.length === 0 ? (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 dark:border-blue-500/20 dark:bg-blue-500/5">
              <FaSpinner className="animate-spin text-sm text-blue-500" />

              <div>
                <p className="text-xs font-bold text-blue-700 dark:text-blue-400">
                  Məhsullar yüklənir...
                </p>

                <p className="mt-0.5 text-[10px] text-blue-600 dark:text-blue-300">
                  Məhsullar hazırlanır. Zəhmət olmasa,
                  gözləyin.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {Array.from({
                length: 12,
              }).map(
                (_, index) => (
                  <SkeletonCard
                    key={index}
                  />
                )
              )}
            </div>
          </>
        ) : error ? (
          <div className="grid grid-cols-2">
            <ErrorBox />
          </div>
        ) : data.length ===
          0 ? (
          <div className="grid grid-cols-2">
            <EmptyData />
          </div>
        ) : (
          <>
            {/* PAGE REFRESH / LOADING */}

            {loading && (
              <div className="mb-4 flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 dark:border-blue-500/20 dark:bg-blue-500/5">
                <FaSpinner className="animate-spin text-sm text-blue-500" />

                <div>
                  <p className="text-xs font-bold text-blue-700 dark:text-blue-400">
                    Məhsullar yenilənir...
                  </p>

                  <p className="mt-0.5 text-[10px] text-blue-600 dark:text-blue-300">
                    Seçdiyiniz filtr və ya səhifəyə uyğun
                    məhsullar gətirilir.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {data.map(
                (product) => (
                  <ProductCard
                    key={
                      product?._id
                    }
                    product={
                      product
                    }
                  />
                )
              )}
            </div>

            {/* LOADING SKELETONS */}

            {loading && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {Array.from({
                  length: 6,
                }).map(
                  (_, index) => (
                    <SkeletonCard
                      key={`loading-${index}`}
                    />
                  )
                )}
              </div>
            )}

            {/* PAGINATION */}

            {!loading &&
              totalPages >
                1 && (
                <div className="mt-10 flex items-center justify-center gap-3">
                  {/* PREVIOUS */}

                  <button
                    type="button"
                    disabled={
                      page <= 1
                    }
                    onClick={
                      previousPage
                    }
                    className="
                      flex h-11 items-center
                      gap-2 rounded-xl
                      border border-gray-200
                      bg-white px-4
                      text-xs font-bold
                      text-gray-700
                      transition
                      hover:border-gray-300
                      hover:bg-gray-50
                      disabled:cursor-not-allowed
                      disabled:opacity-40
                      dark:border-white/[0.08]
                      dark:bg-[#111214]
                      dark:text-white
                      dark:hover:border-white/[0.15]
                    "
                  >
                    <FaArrowRight className="rotate-180 text-[9px]" />

                    <span className="hidden sm:inline">
                      Əvvəlki səhifə
                    </span>

                    <span className="sm:hidden">
                      Əvvəlki
                    </span>
                  </button>

                  {/* PAGE */}

                  <div className="flex h-11 min-w-[90px] items-center justify-center rounded-xl bg-gray-900 px-4 text-xs font-black text-white dark:bg-white dark:text-black">
                    {page}

                    <span className="mx-1 text-white/40 dark:text-black/40">
                      /
                    </span>

                    {totalPages}
                  </div>

                  {/* NEXT */}

                  <button
                    type="button"
                    disabled={
                      page >=
                      totalPages
                    }
                    onClick={
                      nextPage
                    }
                    className="
                      group flex h-11
                      items-center gap-2
                      rounded-xl
                      border border-gray-200
                      bg-white px-4
                      text-xs font-bold
                      text-gray-700
                      transition
                      hover:border-gray-300
                      hover:bg-gray-50
                      disabled:cursor-not-allowed
                      disabled:opacity-40
                      dark:border-white/[0.08]
                      dark:bg-[#111214]
                      dark:text-white
                      dark:hover:border-white/[0.15]
                    "
                  >
                    <span className="hidden sm:inline">
                      Növbəti səhifə
                    </span>

                    <span className="sm:hidden">
                      Növbəti
                    </span>

                    <FaArrowRight className="text-[9px] transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              )}

            {/* END */}

            {!loading &&
              page >=
                totalPages &&
              data.length >
                0 && (
                <div className="mt-8 flex justify-center">
                  <div className="flex items-center gap-3 rounded-full bg-gray-100 px-5 py-2.5 dark:bg-white/[0.04]">
                    <FaCheckCircle className="text-[11px] text-emerald-500" />

                    <span className="text-[10px] font-bold text-gray-500">
                      Bütün məhsullar göstərildi
                    </span>
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    </main>
  );
};

export default Cart;
